import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Fieldset, Input, Modal, toast } from '@feathr/components';
import DonationFormsSelect from '@feathr/extender/components/DonationFormsSelect';
import { useTimeFrameSelect } from '@feathr/extender/hooks';
import { useStore, useUser } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import DataFieldsSelect from './DataFieldsSelect';
import DateRangeSelect from './DateRangeSelect';

import * as styles from './ExportDonationsModal.css';

interface ISelectOption {
  id: string;
  name: string;
}

interface IProps {
  opened: boolean;
  onClose: () => void;
}

function ExportDonationsModal({ opened, onClose }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { DonationForms } = useStore();
  const user = useUser();

  const [email, setEmail] = useState<string>(user.email);
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [selectedOptionalFields, setSelectedOptionalFields] = useState<ISelectOption[]>([]);
  const [customDateRange, setCustomDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined,
  ]);
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all_time');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { options: timeFrameOptions } = useTimeFrameSelect();

  function handleEmailBlur(): void {
    setEmailHadFocus(true);
  }

  function handleChangeEmail(newEmail?: string): void {
    setEmail(newEmail ?? '');
    setEmailHadFocus(true);
  }

  function handleSelectForms(values?: string[]): void {
    setSelectedForms(values ?? []);
  }

  function handleSelectOptionalFields(values: ISelectOption[]): void {
    setSelectedOptionalFields(values);
  }

  function handleDateRangeChange(option: { id: string }): void {
    setSelectedDateRange(option.id);
  }

  function handleCustomDateRangeChange(value: [string | undefined, string | undefined]): void {
    setCustomDateRange(value);
  }

  function validation(): Record<'email' | 'dateRange', string[] | undefined> | undefined {
    const constraints: Record<string, unknown> = {
      email: {
        presence: {
          allowEmpty: false,
          message: '^Email address cannot be empty.',
        },
        email: {
          message: '^Email address is invalid.',
        },
      },
    };

    // Add date validation if custom range is selected
    if (selectedDateRange === 'custom') {
      const startDate = customDateRange[0];
      const endDate = customDateRange[1];
      const today = new Date().toISOString().split('T')[0];

      if (!startDate || !endDate) {
        return {
          email: validate({ email }, { email: constraints.email })?.email,
          dateRange: ['^Both start and end dates are required for custom range.'],
        };
      }

      if (startDate > endDate) {
        return {
          email: validate({ email }, { email: constraints.email })?.email,
          dateRange: ['^Start date must be before end date.'],
        };
      }

      if (startDate > today || endDate > today) {
        return {
          email: validate({ email }, { email: constraints.email })?.email,
          dateRange: ['^Dates cannot be in the future.'],
        };
      }
    }

    return validate({ email }, { email: constraints.email });
  }

  const validationErrors = validation();

  function resetFormState(): void {
    setEmail(user.email);
    setEmailHadFocus(false);
    setSelectedForms([]);
    setSelectedOptionalFields([]);
    setCustomDateRange([undefined, undefined]);
    setSelectedDateRange('all_time');
  }

  function handleClose(): void {
    resetFormState();
    onClose();
  }

  async function handleConfirm(): Promise<void> {
    try {
      setIsLoading(true);

      let startDate: string | undefined;
      let endDate: string | undefined;

      if (selectedDateRange === 'all_time') {
        startDate = undefined;
        endDate = undefined;
      } else if (selectedDateRange === 'custom') {
        startDate = customDateRange[0];
        endDate = customDateRange[1];
      } else {
        const selectedOption = timeFrameOptions.find((opt) => opt.id === selectedDateRange);
        if (selectedOption?.value && Array.isArray(selectedOption.value)) {
          startDate = selectedOption.value[0];
          endDate = selectedOption.value[1];
        }
      }

      await DonationForms.exportDonations({
        email,
        start: startDate,
        end: endDate,
        form_ids: selectedForms.length > 0 ? selectedForms : undefined,
        fields: selectedOptionalFields.map((field) => field.id),
      });

      toast(t('Your export is on the way! Check your email inbox in a few minutes.'), {
        type: 'success',
      });

      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast(t('An error occurred while exporting the data: {{- error}}', { error: errorMessage }), {
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }

  const getModalRightActions = (): JSX.Element[] => {
    return [
      <Button disabled={isLoading} key={'cancel'} onClick={handleClose}>
        {t('Cancel')}
      </Button>,
      <Button
        disabled={
          isLoading ||
          validationErrors?.email !== undefined ||
          validationErrors?.dateRange !== undefined
        }
        isLoading={isLoading}
        key={'confirm'}
        onClick={handleConfirm}
        type={'primary'}
      >
        {t('Send Export to Email')}
      </Button>,
    ];
  };

  return (
    <Modal
      allowOverflow
      description={t(
        'Customize your export settings below. Your CSV file will be delivered via email with a secure download link since exporting may take a few minutes to process.',
      )}
      onClose={handleClose}
      opened={opened}
      rightActions={getModalRightActions()}
      title={t('Export donation data')}
    >
      <Fieldset className={styles.fieldset}>
        <Input
          label={t('Email address')}
          onBlur={handleEmailBlur}
          onChange={handleChangeEmail}
          placeholder={t('firstname.lastname@nonprofit.org')}
          type={'text'}
          validationError={emailHadFocus ? flattenError(validationErrors?.email) : undefined}
          value={email}
        />
        <div className={styles.donationFormsSelect}>
          <DonationFormsSelect isMulti={true} onChange={handleSelectForms} value={selectedForms} />
        </div>
        <DataFieldsSelect
          onSelectOptionalFields={handleSelectOptionalFields}
          selectedOptionalFields={selectedOptionalFields}
        />
        <DateRangeSelect
          customDateRange={customDateRange}
          onCustomDateRangeChange={handleCustomDateRangeChange}
          onDateRangeChange={handleDateRangeChange}
          selectedDateRange={selectedDateRange}
          validationError={validationErrors?.dateRange}
        />
      </Fieldset>
    </Modal>
  );
}

export default observer(ExportDonationsModal);
